import { Calendar, Clock, CheckCircle2, XCircle, Circle } from 'lucide-react';
import Badge from '@/components/common/Badge';
import Button from '@/components/common/Button';
import { useEventsForEmail } from '@/hooks/useEvents';
import { formatDate } from '@/utils/formatters';

const EVENT_TYPE_LABELS = {
  deadline: 'Deadline',
  appointment: 'Appointment',
  sale_end: 'Sale End',
  reminder: 'Reminder'
};

const EVENT_TYPE_COLORS = {
  deadline: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200',
  appointment: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200',
  sale_end: 'bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-200',
  reminder: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-950 dark:text-yellow-200'
};

const STATUS_ICONS = {
  pending: Circle,
  completed: CheckCircle2,
  cancelled: XCircle
};

const STATUS_COLORS = {
  pending: 'text-blue-600 dark:text-blue-400',
  completed: 'text-green-600 dark:text-green-400',
  cancelled: 'text-gray-400 dark:text-gray-600'
};

export default function EventLinks({ emailId, onEventClick }) {
  const { data: events = [], isLoading } = useEventsForEmail(emailId);

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Loading events...</div>;
  }

  if (events.length === 0) {
    return (
      <div className="text-sm text-muted-foreground">
        No events linked to this email
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {events.map(event => {
        const StatusIcon = STATUS_ICONS[event.status] || Circle;
        const eventDate = new Date(event.event_date);
        const isUpcoming = eventDate > new Date() && event.status === 'pending';
        const isPast = eventDate < new Date() && event.status === 'pending';

        return (
          <div
            key={event.id}
            className={`p-3 rounded-lg border ${
              isUpcoming ? 'bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800' :
              isPast ? 'bg-orange-50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-800' :
              'bg-background'
            }`}
          >
            <div className="flex items-start gap-3">
              <StatusIcon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${STATUS_COLORS[event.status]}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  {event.event_type && (
                    <Badge className={`${EVENT_TYPE_COLORS[event.event_type] || ''} text-xs`}>
                      {EVENT_TYPE_LABELS[event.event_type] || event.event_type}
                    </Badge>
                  )}
                  {event.relevance && (
                    <Badge variant="outline" className="text-xs">
                      {event.relevance}
                    </Badge>
                  )}
                </div>
                <h5 className="font-medium text-sm mb-1">{event.title}</h5>
                {event.description && (
                  <p className="text-sm text-muted-foreground mb-2">{event.description}</p>
                )}
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {formatDate(event.event_date)}
                  </div>
                  {isUpcoming && (
                    <span className="text-blue-600 dark:text-blue-400 font-medium">
                      Upcoming
                    </span>
                  )}
                  {isPast && event.status === 'pending' && (
                    <span className="text-orange-600 dark:text-orange-400 font-medium">
                      Overdue
                    </span>
                  )}
                </div>
              </div>
              {onEventClick && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEventClick(event.id)}
                  className="h-7 px-2 flex-shrink-0"
                >
                  View
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
